<template>
  <div class="h-100 d-flex flex-column">
    <v-list density="compact" class="flex-grow-1 overflow-y-auto">

    <template v-if="auth.user.value!.is_defender">
      <div class="mb-4">
        <div v-show="(allLeakprofiles === undefined || (allLeakprofiles?.length || 0) > 0)">
          <v-list-subheader v-if="!isDrawerCollapsed">
            <span>Leak Profiles</span>
            <s-btn-icon
              size="small"
              variant="flat"
              color="secondary"
              density="compact"
              class="ml-2"
              :to="`/leakprofiles/new/`"
            >
              <v-icon icon="mdi-plus" />
              <s-tooltip activator="parent" location="top">Create Leak Profile</s-tooltip>
            </s-btn-icon>
          </v-list-subheader>
          <template v-if="isDrawerCollapsed">
            <div class="d-flex justify-center py-2">
              <s-leak-profile-selection
                ref="leakProfileSelectionRef"
                rail
                :model-value="leakProfile"
                @update:model-value="switchLeakProfile"
                :initial-id="router.currentRoute.value.params.leakprofileId as string"
                variant="underlined"
                density="compact"
              />
            </div>
            <v-divider class="mb-2" />
          </template>
          <v-list-item
            v-else
            class="align-start"
          >
            <s-leak-profile-selection
              ref="leakProfileSelectionRef"
              :model-value="leakProfile"
              @update:model-value="switchLeakProfile"
              :initial-id="router.currentRoute.value.params.leakprofileId as string"
              variant="underlined"
              density="compact"
              class="mb-4"
            />
            <template #append v-if="mainMenuStore.leakProfile">
              <s-btn-icon
                size="small"
                color="light"
                density="compact"
                :to="`/leakprofiles/${leakProfile?.id}/`"
              >
                <v-icon icon="mdi-pencil" />
                <s-tooltip activator="parent" location="top">Edit Leak Profile</s-tooltip>
              </s-btn-icon>
            </template>
          </v-list-item>

          <v-list-item
            :to="`/leakprofiles/${leakProfile?.id}/accounts/`"
            :title="isDrawerCollapsed ? undefined : 'Accounts'"
            :aria-label="'Accounts'"
            prepend-icon="mdi-form-textbox-password"
            :disabled="!leakProfile"
            @click="$emit('navigate')"
          />
          <v-list-item
            :to="`/leakprofiles/${leakProfile?.id}/alerts/`"
            :title="isDrawerCollapsed ? undefined : 'Alerts'"
            :aria-label="'Alerts'"
            prepend-icon="mdi-alarm-light"
            :disabled="!leakProfile"
            @click="$emit('navigate')"
          >
            <template #append>
              <v-badge :content="leakProfile?.open_alert_count || '0'" inline />
            </template>
          </v-list-item>
          <v-list-item
            :to="`/leakprofiles/${leakProfile?.id}/reports/`"
            :title="isDrawerCollapsed ? undefined : 'Reports'"
            :disabled="!leakProfile"
            prepend-icon="mdi-file-document"
            :aria-label="'Reports'"
            @click="$emit('navigate')"
          />
        </div>
        <template v-if="allLeakprofiles?.length === 0">
          <v-list-subheader v-if="!isDrawerCollapsed" title="Setup Defender"/>
          <v-divider v-if="!isDrawerCollapsed" />
          <v-list-item
            :to="`/get-started/`"
            :title="isDrawerCollapsed ? undefined : 'Get Started'"
            :aria-label="'Get Started'"
            prepend-icon="mdi-rocket"
            @click="$emit('navigate')"
          />
        </template>
        <v-list-item
          to="/domains/"
          prepend-icon="mdi-earth"
          :title="isDrawerCollapsed ? undefined : 'Domains'"
          :aria-label="'Domains'"
          @click="$emit('navigate')"
        />
        <v-list-item
          to="/defender-subscriptions/"
          prepend-icon="mdi-credit-card"
          :aria-label="'Subscriptions'"
          @click="$emit('navigate')"
        >
          <template #title v-if="!isDrawerCollapsed">
            Subscriptions
          </template>
        </v-list-item>
      </div>
    </template>

    <template v-if="auth.user.value!.is_attacker">
      <div class="mb-4">
        <v-list-subheader v-if="!isDrawerCollapsed" title="Attacker"/>
        <v-divider v-if="!isDrawerCollapsed" />
        <v-list-item 
          to="/search/" 
          :title="isDrawerCollapsed ? undefined : 'Search'"
          :aria-label="'Search'"
          prepend-icon="mdi-magnify" 
          @click="$emit('navigate')"
        />
        <v-list-item
          to="/attacker-subscriptions/" 
          :title="isDrawerCollapsed ? undefined : 'Subscriptions'"
          :aria-label="'Subscriptions'"
          prepend-icon="mdi-credit-card" 
          @click="$emit('navigate')"
        />
      </div>
    </template>

    <v-list-subheader
      v-if="!isDrawerCollapsed"
      title="Management"
    />
    <v-divider v-if="!isDrawerCollapsed" />
    <v-list-item
      to="/users/"
      :disabled="mainMenuStore.leakProfile?.defender_subscription === 'free'"
      prepend-icon="mdi-account"
      :aria-label="'Users'"
      @click="$emit('navigate')"
    >
      <template #title v-if="!isDrawerCollapsed">
        <pro-info>Users</pro-info>
      </template>
    </v-list-item>
    <v-list-item
      to="/development/"
      prepend-icon="mdi-code-braces-box"
      :aria-label="'Development'"
      @click="$emit('navigate')"
    >
      <template #title v-if="!isDrawerCollapsed">
        Development
      </template>
    </v-list-item>

    <template v-if="auth.user.value!.is_superuser">
      <v-list-subheader v-if="!isDrawerCollapsed" title="Admin Area" class="mt-8" /> 
      <v-divider v-if="!isDrawerCollapsed" />
      <v-list-item 
        v-if="auth.user.value!.is_superuser" 
        href="/admin/" 
        :title="isDrawerCollapsed ? undefined : 'Administration'"
        :aria-label="'Administration'"
        prepend-icon="mdi-file-document"
        @click="$emit('navigate')"
      />
    </template>
    </v-list>

    <div class="mt-auto">
      <v-divider />
      <div class="d-flex justify-center py-1">
        <v-btn
          icon
          size="small"
          variant="text"
          color="secondary"
          @click.stop="cycleDrawerMode"
          :aria-label="drawerModeLabel"
        >
          <v-icon :icon="drawerModeIcon" />
          <s-tooltip activator="parent" location="top">{{ drawerModeLabel }}</s-tooltip>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const auth = useAuth();
const router = useRouter();
const mainMenuStore = useMainMenuStore();
const display = useDisplay();

defineEmits<{
  navigate: []
}>();

const leakProfile = computed({
  get: () => mainMenuStore.leakProfile,
  set: (val: LeakProfile|null) => { mainMenuStore.leakProfile = val; },
});

function switchLeakProfile(newLeakProfile: LeakProfileShort|null|undefined) {
  if (!newLeakProfile) {
    return;
  }

  const oldRoute = router.currentRoute.value;
  const oldLeakprofileId = oldRoute.params.leakprofileId as string|undefined;
  if (oldLeakprofileId && oldLeakprofileId !== newLeakProfile.id) {
    // Navigate to new leak profile
    let path = oldRoute.fullPath.replaceAll(oldLeakprofileId, newLeakProfile.id);
    if (Object.entries(router.currentRoute.value.params).length > 1) {
      // If user was in a sub-page, navigate to the parent page
      path = path.split('/').slice(0, 4).join('/');
      router.push(oldRoute.fullPath.replaceAll(oldLeakprofileId, newLeakProfile.id));
    }
    router.push(path);
  } else {
    // Start page: alerts page of first leak profile
    router.replace(`/leakprofiles/${newLeakProfile.id}/accounts/`);
  }
}

const leakProfileSelectionRef = ref<any>();
const allLeakprofiles = computed<LeakProfile[] | undefined>(() => leakProfileSelectionRef.value?.leakprofiles || undefined);

const isDrawerCollapsed = computed(() => {
  if (mainMenuStore.drawerMode === 'collapsed') return true;
  if (mainMenuStore.drawerMode === 'expanded') return false;
  return display.mdAndDown.value;
});

const drawerModeIcon = computed(() => {
  if (mainMenuStore.drawerMode === 'collapsed') return 'mdi-pin';
  if (mainMenuStore.drawerMode === 'expanded') return 'mdi-pin-outline';
  return 'mdi-pin-off-outline';
});

const drawerModeLabel = computed(() => {
  if (mainMenuStore.drawerMode === 'collapsed') return 'Pinned collapsed';
  if (mainMenuStore.drawerMode === 'expanded') return 'Pinned expanded';
  return 'Auto drawer size';
});

function cycleDrawerMode() {
  mainMenuStore.drawerMode =
    mainMenuStore.drawerMode === 'auto'
      ? 'collapsed'
      : mainMenuStore.drawerMode === 'collapsed'
        ? 'expanded'
        : 'auto';
}
watch([() => router.currentRoute.value.params.leakprofileId], async () => {
  // Update leak profile in mainMenuStore if route changes
  const lpId = router.currentRoute.value.params.leakprofileId as string|undefined;
  if (lpId && leakProfile.value?.id !== lpId) {
    leakProfile.value = await $fetch(`/api/v1/leakprofiles/${lpId}/`);
  }
}, { immediate: true });

async function redirect() {
  if (!auth.user.value || !auth.user.value.is_defender) {
    await nextTick();
    router.replace({ path: "/search/" });
  } else if (allLeakprofiles.value === undefined) {
    return;
  } else if (leakProfile.value) {
    await nextTick();
    router.push(`/leakprofiles/${leakProfile.value.id}/accounts/`);
  } else if (allLeakprofiles.value.length > 0) {
    switchLeakProfile(allLeakprofiles.value[0]!);
  } else {
    await nextTick();
    router.replace({ path: "/get-started/" });
  }
}

watch(
  () => router.currentRoute.value,
  () => {
    if (router.currentRoute.value.name === "leakprofiles") {
      // Call redirect if sent to /leakprofiles/ via router.push
      redirect();
    }
  }
);

watch(allLeakprofiles, async () => {
  if (router.currentRoute.value.name === "leakprofiles") {
    redirect();
  } else if (allLeakprofiles.value && allLeakprofiles.value.length > 0 && !leakProfile.value) {
    await nextTick();
    leakProfile.value = allLeakprofiles.value[0]!;
  }
}, { immediate: true });
</script>
